import React, { useState } from 'react';
import { Activity } from '../types';
import { SaveIcon, XIcon, AlertCircleIcon } from 'lucide-react';
import { useToast } from '../components/Toast';
interface ActivityFormPageProps {
  activity?: Activity;
  onSave: () => void;
  onCancel: () => void;
}
export function ActivityFormPage({
  activity,
  onSave,
  onCancel
}: ActivityFormPageProps) {
  const isEdit = !!activity;
  const {
    addToast
  } = useToast();
  const [formData, setFormData] = useState({
    code: activity?.code || `OPS-SHCCC-${new Date().toISOString().split('T')[0].replace(/-/g, '')}-00X`,
    title: activity?.title || '',
    category: activity?.category || 'Operasi',
    unit: activity?.unit || 'Tim Operasi Siber',
    personInCharge: activity?.personInCharge || '',
    priority: activity?.priority || 'Sedang',
    status: activity?.status || 'Draft',
    classification: activity?.classification || 'Biasa',
    location: activity?.location || '',
    startDate: activity?.startDate || '',
    dueDate: activity?.dueDate || '',
    instructionReference: activity?.instructionReference || '',
    expectedOutput: activity?.expectedOutput || '',
    description: activity?.description || ''
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name
    } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
  };
  // Validation logic
  const errors: Record<string, string> = {};
  if (!formData.title.trim()) errors.title = 'Judul kegiatan wajib diisi';
  if (!formData.startDate) errors.startDate = 'Tanggal mulai wajib diisi';
  if (!formData.dueDate) errors.dueDate = 'Target selesai wajib diisi';
  if (formData.startDate && formData.dueDate && formData.dueDate < formData.startDate) {
    errors.dueDate = 'Target selesai tidak boleh sebelum tanggal mulai';
  }
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mark all as touched to show errors
    const allTouched = Object.keys(formData).reduce((acc, key) => ({
      ...acc,
      [key]: true
    }), {});
    setTouched(allTouched);
    if (Object.keys(errors).length > 0) {
      addToast('Mohon perbaiki error pada formulir sebelum menyimpan.', 'error');
      return;
    }
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      addToast(`Data kegiatan berhasil ${isEdit ? 'diperbarui' : 'disimpan'}.`, 'success');
      onSave();
    }, 800);
  };
  const getInputClass = (name: string) => {
    const baseClass = 'w-full px-3 py-2 border rounded bg-[#0B0F1A] text-[#E2E8F0] placeholder-[#475569] text-sm transition-colors';
    if (touched[name] && errors[name]) {
      return `${baseClass} border-[#FF3B3B] focus:ring-1 focus:ring-[#FF3B3B] focus:border-[#FF3B3B]`;
    }
    return `${baseClass} border-[#1E3A5F] focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF]`;
  };
  const labelClasses = 'block text-xs font-semibold text-[#94A3B8] mb-1.5';
  return <div className="max-w-5xl mx-auto pb-12" data-id="element-274">
      <div className="mb-6" data-id="element-275">
        <h2 className="text-2xl font-bold text-[#E2E8F0] uppercase tracking-wide flex items-center" data-id="element-276">
          <span className="w-2 h-6 bg-[#00D4FF] mr-3" data-id="element-277"></span>
          {isEdit ? 'EDIT KEGIATAN OPERASIONAL' : 'TAMBAH KEGIATAN BARU'}
        </h2>
        <p className="text-[#94A3B8] text-sm mt-2" data-id="element-278">
          Isi formulir di bawah ini dengan lengkap dan benar.
        </p>
      </div>

      {Object.keys(errors).length > 0 && Object.keys(touched).length > 0 && <div className="mb-6 p-4 bg-[#FF3B3B]/10 border border-[#FF3B3B]/30 rounded flex items-start gap-3" data-id="element-279">
          <AlertCircleIcon className="w-5 h-5 text-[#FF3B3B] shrink-0 mt-0.5" data-id="element-280" />
          <div data-id="element-281">
            <h4 className="text-sm font-bold text-[#FF3B3B] uppercase mb-1" data-id="element-282">
              Terdapat Error pada Formulir
            </h4>
            <ul className="text-sm text-[#E2E8F0] list-disc list-inside space-y-1" data-id="element-283">
              {Object.values(errors).map((err, i) => <li key={i} data-id="element-284">{err}</li>)}
            </ul>
          </div>
        </div>}

      <form onSubmit={handleSubmit} className="bg-[#111827] rounded shadow-lg border border-[#1E3A5F] overflow-hidden relative" data-id="element-285">
        <div className="absolute inset-0 cyber-grid opacity-5 pointer-events-none" data-id="element-286"></div>

        <div className="p-6 md:p-8 space-y-8 relative z-10" data-id="element-287">
          {/* Section 1: Identitas Utama */}
          <div data-id="element-288">
            <h3 className="text-sm font-semibold text-[#00D4FF] border-b border-[#1E3A5F] pb-2 mb-4 uppercase tracking-wide flex items-center" data-id="element-289">
              <span className="w-1.5 h-1.5 bg-[#00D4FF] mr-2 rounded-full" data-id="element-290"></span>
              IDENTITAS UTAMA
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-291">
              <div data-id="element-292">
                <label className={labelClasses} data-id="element-293">
                  Kode Kegiatan <span className="text-[#FF3B3B]" data-id="element-294">*</span>
                </label>
                <input type="text" name="code" value={formData.code} onChange={handleChange} onBlur={handleBlur} readOnly className={`${getInputClass('code')} font-mono text-[#00D4FF] opacity-70 cursor-not-allowed`} data-id="element-295" />
              </div>
              <div className="md:col-span-2" data-id="element-296">
                <label className={labelClasses} data-id="element-297">
                  Judul Kegiatan <span className="text-[#FF3B3B]" data-id="element-298">*</span>
                </label>
                <input type="text" name="title" value={formData.title} onChange={handleChange} onBlur={handleBlur} className={getInputClass('title')} placeholder="Contoh: Monitoring Pelaksanaan Operasi..." data-id="element-299" />
                {touched.title && errors.title && <p className="text-xs text-[#FF3B3B] mt-1" data-id="element-300">{errors.title}</p>}
              </div>
              <div data-id="element-301">
                <label className={labelClasses} data-id="element-302">Kategori</label>
                <select name="category" value={formData.category} onChange={handleChange} className={getInputClass('category')} data-id="element-303">
                  <option data-id="element-304">Operasi</option>
                  <option data-id="element-305">Latihan</option>
                  <option data-id="element-306">Evaluasi</option>
                  <option data-id="element-307">Pengembangan</option>
                  <option data-id="element-308">Koordinasi</option>
                  <option data-id="element-309">Audit</option>
                </select>
              </div>
              <div data-id="element-310">
                <label className={labelClasses} data-id="element-311">Klasifikasi</label>
                <select name="classification" value={formData.classification} onChange={handleChange} className={getInputClass('classification')} data-id="element-312">
                  <option data-id="element-313">Biasa</option>
                  <option data-id="element-314">Terbatas</option>
                  <option data-id="element-315">Rahasia</option>
                  <option data-id="element-316">Sangat Rahasia</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section 2: Pelaksanaan */}
          <div data-id="element-317">
            <h3 className="text-sm font-semibold text-[#00D4FF] border-b border-[#1E3A5F] pb-2 mb-4 uppercase tracking-wide flex items-center" data-id="element-318">
              <span className="w-1.5 h-1.5 bg-[#00D4FF] mr-2 rounded-full" data-id="element-319"></span>
              PELAKSANAAN
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-320">
              <div data-id="element-321">
                <label className={labelClasses} data-id="element-322">Unit Pelaksana</label>
                <select name="unit" value={formData.unit} onChange={handleChange} className={getInputClass('unit')} data-id="element-323">
                  <option data-id="element-324">Tim Operasi Siber</option>
                  <option data-id="element-325">Tim Analisis Ancaman</option>
                  <option data-id="element-326">Tim Pertahanan Jaringan</option>
                  <option data-id="element-327">Tim Intelijen Siber</option>
                  <option data-id="element-328">Staf Perencanaan</option>
                </select>
              </div>
              <div data-id="element-329">
                <label className={labelClasses} data-id="element-330">Penanggung Jawab</label>
                <input type="text" name="personInCharge" value={formData.personInCharge} onChange={handleChange} className={getInputClass('personInCharge')} placeholder="Nama / Pangkat" data-id="element-331" />
              </div>
              <div data-id="element-332">
                <label className={labelClasses} data-id="element-333">
                  Tanggal Mulai <span className="text-[#FF3B3B]" data-id="element-334">*</span>
                </label>
                <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} onBlur={handleBlur} className={`${getInputClass('startDate')} font-mono`} data-id="element-335" />
                {touched.startDate && errors.startDate && <p className="text-xs text-[#FF3B3B] mt-1" data-id="element-336">
                    {errors.startDate}
                  </p>}
              </div>
              <div data-id="element-337">
                <label className={labelClasses} data-id="element-338">
                  Target Selesai <span className="text-[#FF3B3B]" data-id="element-339">*</span>
                </label>
                <input type="date" name="dueDate" value={formData.dueDate} onChange={handleChange} onBlur={handleBlur} className={`${getInputClass('dueDate')} font-mono`} data-id="element-340" />
                {touched.dueDate && errors.dueDate && <p className="text-xs text-[#FF3B3B] mt-1" data-id="element-341">
                    {errors.dueDate}
                  </p>}
              </div>
              <div className="md:col-span-2" data-id="element-342">
                <label className={labelClasses} data-id="element-343">Lokasi</label>
                <input type="text" name="location" value={formData.location} onChange={handleChange} className={getInputClass('location')} data-id="element-344" />
              </div>
            </div>
          </div>

          {/* Section 3: Status & Detail */}
          <div data-id="element-345">
            <h3 className="text-sm font-semibold text-[#00D4FF] border-b border-[#1E3A5F] pb-2 mb-4 uppercase tracking-wide flex items-center" data-id="element-346">
              <span className="w-1.5 h-1.5 bg-[#00D4FF] mr-2 rounded-full" data-id="element-347"></span>
              STATUS & DETAIL
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-348">
              <div data-id="element-349">
                <label className={labelClasses} data-id="element-350">Status Saat Ini</label>
                <select name="status" value={formData.status} onChange={handleChange} className={getInputClass('status')} data-id="element-351">
                  <option data-id="element-352">Draft</option>
                  <option data-id="element-353">Baru</option>
                  <option data-id="element-354">Berjalan</option>
                  <option data-id="element-355">Menunggu Verifikasi</option>
                  <option data-id="element-356">Tertunda</option>
                  <option data-id="element-357">Selesai</option>
                  <option data-id="element-358">Dibatalkan</option>
                </select>
              </div>
              <div data-id="element-359">
                <label className={labelClasses} data-id="element-360">Prioritas</label>
                <select name="priority" value={formData.priority} onChange={handleChange} className={getInputClass('priority')} data-id="element-361">
                  <option data-id="element-362">Rendah</option>
                  <option data-id="element-363">Sedang</option>
                  <option data-id="element-364">Tinggi</option>
                  <option data-id="element-365">Sangat Tinggi</option>
                </select>
              </div>
              <div className="md:col-span-2" data-id="element-366">
                <label className={labelClasses} data-id="element-367">Dasar Perintah</label>
                <input type="text" name="instructionReference" value={formData.instructionReference} onChange={handleChange} className={getInputClass('instructionReference')} placeholder="Contoh: Surat Perintah No..." data-id="element-368" />
              </div>
              <div className="md:col-span-2" data-id="element-369">
                <div className="flex justify-between items-end mb-1.5" data-id="element-370">
                  <label className="block text-xs font-semibold text-[#94A3B8]" data-id="element-371">
                    Deskripsi Kegiatan
                  </label>
                  <span className="text-[10px] text-[#64748B] font-mono" data-id="element-372">
                    {formData.description.length}/500
                  </span>
                </div>
                <textarea name="description" value={formData.description} onChange={handleChange} maxLength={500} rows={3} className={getInputClass('description')} data-id="element-373"></textarea>
              </div>
              <div className="md:col-span-2" data-id="element-374">
                <div className="flex justify-between items-end mb-1.5" data-id="element-375">
                  <label className="block text-xs font-semibold text-[#94A3B8]" data-id="element-376">
                    Output yang Diharapkan
                  </label>
                  <span className="text-[10px] text-[#64748B] font-mono" data-id="element-377">
                    {formData.expectedOutput.length}/250
                  </span>
                </div>
                <textarea name="expectedOutput" value={formData.expectedOutput} onChange={handleChange} maxLength={250} rows={2} className={getInputClass('expectedOutput')} data-id="element-378"></textarea>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="bg-[#0F1629] px-6 py-4 border-t border-[#1E3A5F] flex justify-end space-x-3 relative z-10" data-id="element-379">
          <button type="button" onClick={onCancel} disabled={isSubmitting} className="flex items-center px-4 py-2 border border-[#1E3A5F] text-[#E2E8F0] bg-[#1A2332] rounded hover:bg-[#1E3A5F] transition-colors font-bold text-xs uppercase disabled:opacity-50" data-id="element-380">
            <XIcon className="w-4 h-4 mr-2" data-id="element-381" />
            BATAL
          </button>
          <button type="submit" disabled={isSubmitting} className="flex items-center px-4 py-2 bg-[#00D4FF] text-[#0B0F1A] rounded hover:bg-[#00D4FF]/90 transition-colors shadow-sm font-bold text-xs uppercase glow-cyan disabled:opacity-50" data-id="element-382">
            {isSubmitting ? <div className="w-4 h-4 border-2 border-[#0B0F1A]/30 border-t-[#0B0F1A] rounded-full animate-spin mr-2" data-id="element-383"></div> : <SaveIcon className="w-4 h-4 mr-2" data-id="element-384" />}
            {isSubmitting ? 'MENYIMPAN...' : 'SIMPAN DATA'}
          </button>
        </div>
      </form>
    </div>;
}